import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { StoreView } from '@wodi/shared';
import { Button, LoadingBlock, Notice } from '@wodi/ui';
import type { ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import { storesQuery } from '../api/stock.ts';
import styles from './stock.module.css';

/** The API's message, which says what happened and what to do; otherwise the fallback. */
export function errorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

const LAST_STORE_KEY = 'wodi.stores.last';

/** The store last opened on this device. Not patient data; a convenience only. */
export function rememberedStoreId(): string | null {
  try {
    return window.localStorage.getItem(LAST_STORE_KEY);
  } catch {
    return null;
  }
}

export function rememberStore(storeId: string): void {
  try {
    window.localStorage.setItem(LAST_STORE_KEY, storeId);
  } catch {
    // Private windows may refuse storage; the page works without it.
  }
}

/** A newly generated key for one create request, so a retry can't post twice. */
export function newRequestKey(): string {
  return crypto.randomUUID();
}

/** Loads the facility's stores and renders the chosen one, with every state handled. */
export function WithStore({
  storeId,
  children,
}: {
  storeId: string;
  children: (store: StoreView, stores: StoreView[]) => ReactNode;
}) {
  const stores = useQuery(storesQuery);
  if (stores.isPending) return <LoadingBlock label="Loading stores" rows={3} />;
  if (stores.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void stores.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(stores.error, "Stores couldn't be loaded. Try again.")}
      </Notice>
    );
  const store = stores.data.find((s) => s.id === storeId);
  if (store === undefined)
    return (
      <Notice tone="info">
        This store isn't open at this facility. <Link to="/stores">Choose a store</Link>.
      </Notice>
    );
  return <>{children(store, stores.data)}</>;
}

export function BackToStore({ storeId, name }: { storeId: string; name: string }) {
  return (
    <p className={styles.meta}>
      <Link to="/stores" search={{ storeId }}>
        Back to {name}
      </Link>
    </p>
  );
}
